import { usePlatformMeta } from '@apparel/react/platform/usePlatformMeta';
import { formatPrice } from '@apparel/ui/productCard/ProductCard';
import { CompactButton, PrimaryButton } from '@components/Buttons';
import { PrimaryButtonProps } from '@components/Buttons/primary';
import { Currency } from '@components/Currency';
import { Block, InlineFlex } from '@components/Layout/Blocks';
import { Col } from '@components/Layout/Col';
import { Row } from '@components/Layout/Row';
import { TopLoader } from '@components/TopLoader';
import { I18n } from '@components/Trans';
import { formatDate } from '@lib/datetime';
import { createStyled } from '@lib/style/createStyled';
import { padding, PaddingProps } from '@lib/style/mixins/padding';
import { getNumberToDecimalPlaces, getOrderTotalAndTax, getOrderTotalDue$ } from '@lib/utils';
import Divider from '@material-ui/core/Divider';
import Paper, { PaperProps } from '@material-ui/core/Paper';
import RootRef from '@material-ui/core/RootRef';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/styles';
import { call } from '@packages/functions';
import { IF } from '@packages/react/lib/if';
import { UserView } from '@packages/user/lib/UserView';
import clsx from 'clsx';
import { reverse } from 'lodash';
import { transparentize } from 'polished';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AddDiscount, DiscountWrap } from './style';

const _noopRef = React.createRef();

interface PricePreviewProps extends PaperProps, Partial<PaddingProps> {
    prices: any;
    loading?: boolean;
    disableSubmit?: boolean;
    noSubmit?: boolean;
    noTerms?: boolean;

    submitText?: any;
    submitRef?: any;
    reverse?: boolean;
    cutPadding?: boolean;
    noTitle?: boolean;
    useCredit?: boolean;
    creditToUse?: number;
    sales_tax_formula?: any;
    isRefund?: boolean;
    refund?: number;
    onUpdateDiscount?: (...args: any) => Promise<void>;
    submitProps?: PrimaryButtonProps;
    totalAmountPaid?: any;
    orderPayments?: any[];
    orderIsPurchased?: boolean;
    isDiscountAble?: boolean;
    marketplaceLayout?: boolean;
    totalPaid?: any;
    isRedoOrder?: boolean;
    isReOrder?: boolean;
}

export const PricePreview = (props: PricePreviewProps) => {
    const {
        loading,
        prices,
        disableSubmit,
        noSubmit,
        noTerms,
        submitText,
        cutPadding,
        useCredit,
        creditToUse,
        sales_tax_formula,
        submitRef,
        submitProps,
        noTitle,
        refund,
        isRefund,
        onUpdateDiscount,
        reverse: reverse$,
        totalAmountPaid,
        orderPayments,
        orderIsPurchased,
        isDiscountAble,
        marketplaceLayout,
        totalPaid,
        isRedoOrder = false,
        isReOrder = false,
        ...rest
    } = props;

    const prices$ = prices || { items: [] };
    const submitRef$ = submitRef || _noopRef;
    const [discountStatus, setDiscountStatus] = useState<any>(false);
    const [discount, setDiscount] = useState<any>(null);
    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const [isEdit, setEdit] = useState<any>(true);
    const meta = usePlatformMeta();
    const onDelete = async () => {
        call(onUpdateDiscount, 0);
        setDiscountStatus(false);
        setDiscount(null);
    };

    const onCancel = () => {
        setEdit(true);
        if (!(prices$.discount > 0)) {
            setDiscountStatus(false);
            setDiscount(null);
        } else {
            setDiscount(prices$.discount);
        }
    };

    const handleDiscountStatus = () => {
        setDiscountStatus(true);
        setEdit(false);
    };

    const handleAddDiscount = async () => {
        call(onUpdateDiscount, discount);
        setEdit(true);
    };

    const onDiscountChange = (e) => {
        setDiscount(e.target.value || prices$.bulk_discount);
    };

    const handleEditDiscount = () => {
        setEdit(false);
    };

    const { total, tax } = useMemo(() => {
        return getOrderTotalAndTax(
            prices$,
            creditToUse,
            useCredit,
            prices$?.coupon_discount,
            sales_tax_formula,
            orderPayments?.length,
        );
    }, [prices$, creditToUse, useCredit, prices$?.coupon_discoun, sales_tax_formula, orderPayments]);
    const getBalance = useMemo(() => {
        return getOrderTotalDue$(total, creditToUse, totalAmountPaid);
    }, [total, creditToUse, totalAmountPaid]);
    const getTotal = useMemo(() => {
        const processingFees =
            orderPayments?.reduce((processingFee, payment) => processingFee + Number(payment.processing_charges), 0) ??
            0;
        return Math.max(total + processingFees, 0);
    }, [total, tax, orderPayments]);

    useEffect(() => {
        if (prices$.discount > 0) {
            setDiscountStatus(true);
        }
    }, [prices$]);
    const isValidDiscountValue = useMemo(() => {
        return discount <= getTotal + prices$.discount;
    }, [discount, prices$.discount]);

    useEffect(() => {
        setIsFormValid(isValidDiscountValue && discount > 0);
    }, [discount, prices$.discount]);
    const getPrivacyText = () => (
        <Typography className={'sidebar__policy'}>
            By clicking continue you are agreeing to the&nbsp;
            <a href={meta.termsLink} target="_blank" rel="noopener noreferrer">
                {meta.title} Terms Of Service.
            </a>
        </Typography>
    );

    let buttonChildes = [
        <Row key={'btn_child_0'} padding={reverse$ ? '0 0 20px 0' : '20px 0 0 0'}>
            <RootRef rootRef={submitRef$}>
                <PrimaryButton fullWidth style={{ margin: 0, height: 48 }} disabled={disableSubmit} {...submitProps}>
                    {submitText}
                </PrimaryButton>
            </RootRef>
        </Row>,

        <Fragment key={'btn_child_1'}>{IF(!noTerms, <Row padding={'18px 0'}>{getPrivacyText()}</Row>)}</Fragment>,

        <Divider key={'btn_child_2'} style={{ marginTop: reverse$ ? 20 : 0 }} />,
    ];

    const discountText = useMemo(
        () =>
            isRedoOrder && !orderPayments?.length
                ? 'Redo Discount'
                : isReOrder && !orderPayments?.length
                ? 'Re-order Discount'
                : 'Added Discount',
        [isRedoOrder, isReOrder, orderPayments?.length],
    );

    if (reverse$) {
        buttonChildes = reverse(buttonChildes);
    }

    let children$ = [
        IF(!noSubmit, <ButtonPanel key={'submit_panel'}>{buttonChildes}</ButtonPanel>),
        <Block key={'content'}>
            <Row padding={cutPadding ? '0 0 8px' : '20px 0'}>
                {!noTitle ? (
                    <Col xs={12} padding={cutPadding ? 0 : '0 0 12px 0'}>
                        <I18n variant={'subtitle2'}>Price Summary</I18n>
                    </Col>
                ) : null}
                <PriceLine value={prices$.subtotal}>Items Total</PriceLine>
                {IF(
                    prices$.addon_cost,
                    <PriceLine value={getNumberToDecimalPlaces(prices$.addon_cost, 2)}>Add-Ons</PriceLine>,
                )}
                {IF(useCredit, <PriceLine value={creditToUse ? creditToUse * -1 : 0}>Credit</PriceLine>)}
                <PriceLine value={prices$.discount * -1}>{discountText}</PriceLine>

                {prices$.coupon_discount > 0 ? (
                    <PriceLine value={prices$.coupon_discount * -1}>
                        <>
                            {prices$.promo_code}&nbsp;
                            <Typography className={'text-secondary price__label'} display={'inline'}>
                                {prices$.promo_discount_label}
                            </Typography>
                            :
                        </>
                    </PriceLine>
                ) : null}
                <PriceLine value={getNumberToDecimalPlaces(prices$.shipping_cost, 2)}>Shipping & Handling</PriceLine>
                <PriceLine value={getNumberToDecimalPlaces(prices$.shipping_method_adds, 2)}>
                    Production Method Fee
                </PriceLine>
                {IF(isRefund, <PriceLine value={refund ? refund * -1 : 0}>Refund</PriceLine>)}
                {tax > 0 ? <PriceLine value={tax}>{`Sales Tax`}</PriceLine> : null}
                {(orderPayments ?? []).map(({ processing_charges }, i) => (
                    <PriceLine key={i} value={processing_charges}>
                        <>
                            Processing Fee{' '}
                            <Typography variant={'caption'} color={'textSecondary'}>
                                (Payment {i + 1})
                            </Typography>
                            :
                        </>
                    </PriceLine>
                ))}
            </Row>
            <Divider />
            <UserView view={'admin,salesman,wholesaler'}>
                {IF(
                    (!orderIsPurchased ?? false) && !isRedoOrder && !isReOrder,
                    IF(
                        discountStatus,
                        <Row>
                            <Col md={6}>
                                <AddDiscount> {!isEdit ? 'Add' : 'Added'} Discount :</AddDiscount>
                            </Col>
                            <Col md={6}>
                                <DiscountWrap>
                                    {!isEdit ? (
                                        <>
                                            <div className="dolor">$</div>
                                            <TextField
                                                className="discount-input"
                                                type="number"
                                                variant="filled"
                                                required
                                                onChange={onDiscountChange}
                                                defaultValue={discount || prices$.discount}
                                                error={!isValidDiscountValue}
                                                helperText={
                                                    !isValidDiscountValue ? 'Amount cannot exceed order total' : ''
                                                }
                                            />
                                        </>
                                    ) : (
                                        <Typography>
                                            $
                                            {discount
                                                ? parseFloat(discount).toFixed(2)
                                                : parseFloat(prices$.discount).toFixed(2)}
                                        </Typography>
                                    )}
                                </DiscountWrap>
                            </Col>
                            {!isEdit ? (
                                <Col
                                    md={12}
                                    style={{ display: 'flex', justifyContent: 'flex-end', margin: '20px 0px' }}
                                >
                                    <CompactButton
                                        className={clsx('discount-action-button', 'cancel-button')}
                                        onClick={onCancel}
                                    >
                                        Cancel
                                    </CompactButton>

                                    <PrimaryButton
                                        onClick={handleAddDiscount}
                                        className={clsx('discount-action-button', 'save-button')}
                                        disabled={!isFormValid}
                                    >
                                        Save
                                    </PrimaryButton>
                                </Col>
                            ) : (
                                !orderPayments?.length && (
                                    <Col
                                        md={12}
                                        style={{ display: 'flex', justifyContent: 'flex-end', margin: '20px 0px' }}
                                    >
                                        <Typography onClick={onDelete} style={{ cursor: 'pointer', marginRight: 10 }}>
                                            Delete
                                        </Typography>
                                        <Typography
                                            className="MuiButton-textPrimary"
                                            style={{ marginLeft: '5px', cursor: 'pointer' }}
                                            onClick={handleEditDiscount}
                                            align={'right'}
                                        >
                                            Edit
                                        </Typography>
                                    </Col>
                                )
                            )}
                        </Row>,
                        IF(
                            !!isDiscountAble && !orderPayments?.length,
                            <Typography
                                onClick={handleDiscountStatus}
                                style={{
                                    cursor: 'pointer',
                                    textAlign: 'right',
                                    width: '100px',
                                    margin: '5px 0px',
                                    marginLeft: 'auto',
                                }}
                                className="MuiButton-textPrimary"
                            >
                                Add Discount
                            </Typography>,
                        ),
                    ),
                )}
            </UserView>

            <Block w={'100%'}>
                <PriceLine padding={cutPadding ? '8px 0 0' : '14px 0 0'} value={getTotal} color={'#e4002b'}>
                    Total
                </PriceLine>
            </Block>

            {orderPayments?.length > 0 && !marketplaceLayout ? (
                <>
                    <Divider style={{ width: '100%' }} />
                    {(orderPayments ?? []).map(({ amount_paid, processing_charges, created_at, status }, i) => (
                        <Block w={'100%'} key={i}>
                            <PriceLine
                                padding={cutPadding ? '8px 0 0' : '14px 0'}
                                value={
                                    (Number(amount_paid ?? 0) + Number(processing_charges)) *
                                    (parseInt(status) === 0 ? 1 : -1)
                                }
                            >{`Payment ${i + 1} ${
                                parseInt(status) === 2 ? '(Pending)' : parseInt(status) === 0 ? '(Rejected)' : ''
                            }`}</PriceLine>
                            <Typography
                                variant={'caption'}
                                color={'textSecondary'}
                                display={'block'}
                                gutterBottom={false}
                            >
                                {formatDate('human', created_at)}
                            </Typography>
                        </Block>
                    ))}
                </>
            ) : null}
            {orderPayments?.length > 0 && !marketplaceLayout ? (
                <PriceLine padding={cutPadding ? '8px 0 0' : '14px 0'} value={getBalance} color={'success'}>
                    {'Balance'}
                </PriceLine>
            ) : null}
            {getBalance === 0 ? (
                <>
                    {!marketplaceLayout ? (
                        <>
                            <Divider style={{ width: '100%' }} />
                            <PriceLine
                                padding={cutPadding ? '8px 0 0' : '14px 0'}
                                value={totalPaid ?? 0}
                                color={'success'}
                            >
                                Total Paid
                            </PriceLine>
                        </>
                    ) : null}
                </>
            ) : null}
        </Block>,
    ];

    if (reverse$) {
        children$ = reverse(children$);
    }

    return (
        <Style elevation={0} {...rest}>
            {children$}
            <TopLoader show={!!loading} t={0} l={0} r={0} b={0} />
        </Style>
    );
};

export const PriceSummaryCreditDiscount = ({ prices, payments = [] }) => {
    const classes = useStyles({ payments });
    const formattedPrice = (price) => <span className={classes.price}>{formatPrice(price ?? 0)}</span>;
    let children;

    if (!!prices?.bulk_discount) {
        children = (
            <Typography variant={'caption'}>
                You are saving {formattedPrice(prices?.bulk_discount)} for ordering items in bulk
            </Typography>
        );
    }

    return (
        <Block key={'credit_and_discount'} padding={'16px'} className={classes.creditDiscountBlock}>
            {payments?.length ? (
                <>
                    {!!prices?.bulk_discount && (
                        <Typography variant={'caption'} display={'block'}>
                            Bulk Savings: {formattedPrice(prices?.bulk_discount)}
                        </Typography>
                    )}
                </>
            ) : (
                children
            )}
        </Block>
    );
};

PricePreview.displayName = 'PricePreview';
PricePreview.defaultProps = {
    submitText: 'Continue',
    submitProps: {
        type: 'submit',
    },
};

const useStyles = makeStyles({
    creditDiscountBlock: {
        backgroundColor: '#f9f9f9',
        borderTop: '1px solid #E0E0E0',
        textAlign: (props: any) => (props.payments?.length ? 'left' : 'center'),
    },
    price: {
        color: '#1e8943',
        fontWeight: 500,
    },
});

const Style = createStyled(
    Paper,
    [padding as any],
    ({ css }) => css`
        && {
            width: 100%;
            position: relative;
            ${padding('0 24px')};
            @media screen and (max-width: 959px) {
                border-radius: 0 !important;
            }
            .text-secondary {
                color: rgba(0, 0, 0, 0.54) !important;
            }

            .sidebar {
                &__policy {
                    &,
                    a {
                        color: ${transparentize(0.46, '#000')};
                        font-size: 12px;
                        text-align: center;
                    }

                    a {
                        width: 100%;
                        font-weight: 500;

                        &:hover {
                            color: ${transparentize(0.2, '#000')};
                        }
                    }
                }
            }
            .discount-action-button {
                font-size: 14px;
                font-weight: 500;
                line-height: 20px;
                letter-spacing: 0.35px;

                &.cancel-button {
                    margin-right: 16px;
                }

                &.save-button {
                    margin-right: 0;
                }

                &.delete-button {
                    color: #c62828;
                    margin-right: 2px;
                }

                &.edit-button {
                    color: ${({ primaryColor }) => primaryColor};
                    margin-right: 0;
                    padding-right: 0;
                }

                & .MuiButton-label {
                    font-size: 14px;
                }
            }
        }
    `,
);

const PriceLine = styled(({ children, value, color, ...props }) => (
    <Row align={'center'} justify={'start'} {...props}>
        <InlineFlex w={'60%'} p={'0 4px 8px 0'}>
            <I18n className={'price__label'} variant={'body1'} after={':'} withTitle>
                {children}
            </I18n>
        </InlineFlex>
        <InlineFlex p={0} justify={'end'} w={'100%'} maxWidth={'40%'}>
            <Typography className={'price__value'} variant={'body2'}>
                {typeof value === 'undefined' ? (
                    '-'
                ) : (
                    <Currency color={color} maximumFractionDigits={4}>
                        {value}
                    </Currency>
                )}
            </Typography>
        </InlineFlex>
    </Row>
))`
    && {
        .price {
            &__label,
            &__value {
                font-size: 14px;
                line-height: 24px;
                letter-spacing: 0.24px;
                color: #222;
            }

            &__label {
                opacity: 0.9;
                white-space: normal;
            }

            &__value {
                width: 100%;
                text-align: right;
            }
        }
    }
`;

PriceLine.displayName = 'PriceLine';
const ButtonPanel = styled.div`
    && {
        @media screen and (max-width: 599px) {
            margin: 0 -24px;
            padding: 0 24px;
            background: #f5f5f5;
        }
    }
`;
